import { useLanguage } from '../../i18n';
import DatePicker from '../DatePicker';
import type { ReleaseTarget } from '../../types';

interface TeamInfo {
  team_id: number;
  team_name?: string;
}

interface BasicInfoSectionProps {
  featureName: string;
  setFeatureName: (value: string) => void;
  releaseDate: string;
  setReleaseDate: (value: string) => void;
  isDemo?: boolean;
  isReEvaluate?: boolean;  // 다시 평가하기 모드 (제목 수정 불가)
  onStartWithGithub?: () => void;
  // 팀/개인 선택
  isTeamPlan?: boolean;  // Team 플랜 여부
  teamInfo?: TeamInfo | null;  // 실제 소속 팀 정보 (없으면 팀 버튼 비활성화)
  saveAsPersonal?: boolean;
  setSaveAsPersonal?: (value: boolean) => void;
  // 평가 대상 선택
  target?: ReleaseTarget;
  setTarget?: (value: ReleaseTarget) => void;
}

const TARGET_OPTIONS: { value: ReleaseTarget; icon: string }[] = [
  { value: 'frontend', icon: '🎨' },
  { value: 'backend', icon: '⚙️' },
  { value: 'fullstack', icon: '🔄' },
  { value: 'infra', icon: '🏗️' },
  { value: 'automation', icon: '🤖' },
];

function BasicInfoSection({
  featureName,
  setFeatureName,
  releaseDate,
  setReleaseDate,
  isDemo = false,
  isReEvaluate = false,
  onStartWithGithub,
  isTeamPlan = false,
  teamInfo,
  saveAsPersonal = false,
  setSaveAsPersonal,
  target = 'frontend',
  setTarget,
}: BasicInfoSectionProps) {
  const { lang, t } = useLanguage();
  const isFeatureNameReadOnly = isDemo || isReEvaluate;
  // Team 플랜이면 항상 표시, 팀이 없으면 팀 버튼만 비활성화
  const showTeamSelector = !isDemo && isTeamPlan && setSaveAsPersonal;
  const hasTeam = !!teamInfo;
  const showTargetSelector = !isDemo && setTarget;

  return (
    <div className="card p-4" data-testid="basic-info-section">
      <div className="text-terminal-muted text-xs mb-3 uppercase tracking-wide">
        # {t.form.basicInfo}
      </div>
      
      {!isDemo && onStartWithGithub && (
        <div className="mb-3 text-xs text-terminal-muted flex items-center justify-between gap-3">
          <div>
            {lang === 'ko'
              ? 'GitHub 커밋 기반으로 평가를 시작하고 싶다면 아래 섹션에서 선택하세요.'
              : 'If you want to start from GitHub commits, select them in the section below.'}
          </div>
          <button
            type="button"
            onClick={() => {
              onStartWithGithub();
            }}
            className="text-status-go hover:text-status-go/90 underline whitespace-nowrap"
          >
            {lang === 'ko' ? 'GitHub로 시작하기 →' : 'Start with GitHub →'}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* 팀/개인 선택 (팀 소속 사용자만) - 세그먼트 컨트롤 스타일 */}
        {showTeamSelector && (
          <div>
            <label className="block text-terminal-muted text-sm mb-2">
              {lang === 'ko' ? '저장 위치' : 'Save to'}
            </label>
            <div className="grid grid-cols-2 gap-2 p-1 bg-terminal-dim/30 rounded-lg border border-terminal-border">
              {/* 개인 */}
              <button
                type="button"
                onClick={() => setSaveAsPersonal(true)}
                className={`relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  saveAsPersonal
                    ? 'bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-500/50 text-violet-300 shadow-lg shadow-violet-500/10'
                    : 'text-terminal-muted hover:text-terminal-text hover:bg-terminal-dim/50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>{lang === 'ko' ? '개인' : 'Personal'}</span>
                {saveAsPersonal && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-violet-400 rounded-full animate-pulse" />
                )}
              </button>
              {/* 팀 */}
              <button
                type="button"
                onClick={() => hasTeam && setSaveAsPersonal(false)}
                disabled={!hasTeam}
                className={`relative flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${
                  !hasTeam
                    ? 'opacity-40 cursor-not-allowed text-terminal-dim'
                    : !saveAsPersonal
                      ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 text-cyan-300 shadow-lg shadow-cyan-500/10'
                      : 'text-terminal-muted hover:text-terminal-text hover:bg-terminal-dim/50'
                }`}
                title={!hasTeam ? (lang === 'ko' ? '팀을 먼저 생성하거나 참여해주세요' : 'Create or join a team first') : undefined}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="truncate">
                  {hasTeam 
                    ? (teamInfo?.team_name || (lang === 'ko' ? '팀' : 'Team'))
                    : (lang === 'ko' ? '팀 없음' : 'No Team')}
                </span>
                {!saveAsPersonal && hasTeam && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                )}
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-terminal-muted">
              {!hasTeam
                ? (lang === 'ko' ? '💡 팀을 생성하거나 초대를 수락하면 팀 히스토리를 사용할 수 있어요' : '💡 Create a team or accept an invite to use team history')
                : saveAsPersonal
                  ? (lang === 'ko' ? '🔒 나만 볼 수 있는 개인 히스토리에 저장됩니다' : '🔒 Saved to your private history')
                  : (lang === 'ko' ? '👥 팀원 모두가 볼 수 있는 팀 히스토리에 저장됩니다' : '👥 Saved to shared team history')}
            </p>
          </div>
        )}

        {/* 평가 대상 선택 */}
        {showTargetSelector && (
          <div>
            <label className="block text-terminal-muted text-sm mb-2">
              {t.releaseTarget?.title || '평가 대상'}
            </label>
            <div className="flex flex-wrap gap-2">
              {TARGET_OPTIONS.map((option) => {
                const isSelected = target === option.value;
                const label = t.releaseTarget?.[option.value] || option.value;
                const desc = t.releaseTarget?.[`${option.value}Desc` as keyof typeof t.releaseTarget] || '';
                
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTarget(option.value)}
                    className={`
                      relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      border
                      ${isSelected
                        ? option.value === 'backend'
                          ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/50 text-amber-300 shadow-lg shadow-amber-500/10'
                          : option.value === 'fullstack'
                            ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-emerald-500/50 text-emerald-300 shadow-lg shadow-emerald-500/10'
                            : option.value === 'infra'
                              ? 'bg-gradient-to-r from-purple-500/20 to-indigo-500/20 border-purple-500/50 text-purple-300 shadow-lg shadow-purple-500/10'
                              : option.value === 'automation'
                                ? 'bg-gradient-to-r from-pink-500/20 to-rose-500/20 border-pink-500/50 text-pink-300 shadow-lg shadow-pink-500/10'
                                : 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-500/50 text-blue-300 shadow-lg shadow-blue-500/10'
                        : 'bg-terminal-dim/30 border-terminal-border text-terminal-muted hover:text-terminal-text hover:border-terminal-muted'
                      }
                    `}
                    title={desc as string}
                  >
                    <span>{option.icon}</span>
                    <span>{label}</span>
                    {isSelected && (
                      <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse ${
                        option.value === 'backend' ? 'bg-amber-400' :
                        option.value === 'fullstack' ? 'bg-emerald-400' :
                        option.value === 'infra' ? 'bg-purple-400' :
                        option.value === 'automation' ? 'bg-pink-400' :
                        'bg-blue-400'
                      }`} />
                    )}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-terminal-muted">
              {target === 'backend' 
                ? (lang === 'ko' ? '⚙️ 백엔드 선택 시 API, 보안, DB 등 백엔드 전용 체크 항목이 표시됩니다' : '⚙️ Backend-specific checks (API, Security, DB) will be shown')
                : target === 'fullstack'
                  ? (lang === 'ko' ? '🔄 프론트엔드 + 백엔드 통합 평가' : '🔄 Combined Frontend + Backend evaluation')
                  : target === 'infra'
                    ? (lang === 'ko' ? '🏗️ 인프라/DevOps 변경사항 평가' : '🏗️ Infrastructure/DevOps changes evaluation')
                    : target === 'automation'
                      ? (lang === 'ko' ? '🤖 자동화/봇 시스템 평가' : '🤖 Automation/Bot system evaluation')
                      : (lang === 'ko' ? '🎨 프론트엔드/UI 변경사항 평가' : '🎨 Frontend/UI changes evaluation')
              }
            </p>
          </div>
        )}
        <div>
          <label className="block text-terminal-muted text-sm mb-1">
            {t.form.featureName}
            <span className="text-red-500 font-bold">*</span>
            {isReEvaluate && (
              <span className="ml-2 text-xs text-status-warning">
                ({lang === 'ko' ? '다시 평가 - 수정 불가' : 'Re-evaluate - Read only'})
              </span>
            )}
          </label>
          <input
            type="text"
            value={featureName}
            onChange={(e) => setFeatureName(e.target.value)}
            placeholder={
              lang === 'ko'
                ? '실시간 레슨 리포트 개선'
                : 'Real-time lesson report improvement'
            }
            className={`terminal-input ${isFeatureNameReadOnly ? 'bg-terminal-input/60 cursor-not-allowed' : ''}`}
            required
            readOnly={isFeatureNameReadOnly}
            data-testid="input-feature-name"
          />
        </div>
        <div>
          <label className="block text-terminal-muted text-sm mb-1">
            {t.form.releaseDate}
            <span className="text-red-500 font-bold">*</span>
          </label>
          <DatePicker value={releaseDate} onChange={setReleaseDate} required />
        </div>
      </div>
    </div>
  );
}

export default BasicInfoSection;

